import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * PortalInstallPrompt
 * -------------------
 * Captures the `beforeinstallprompt` event (Chromium browsers) and surfaces
 * a subtle in-app CTA so customers don't have to hunt in the browser menu.
 * On iOS Safari (which doesn't fire that event), shows an "Add to Home
 * Screen" hint the first time the portal is opened outside standalone mode.
 * Stores dismissal in localStorage so it never nags twice.
 */
const DISMISS_KEY = "gt.portal.installPrompt.dismissed";

export default function PortalInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Already installed / running standalone → don't show anything.
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari (no beforeinstallprompt) — show the manual hint.
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    if (isIOS && isSafari) {
      setIosHint(true);
      setVisible(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice.catch(() => null);
    if (choice?.outcome === "accepted") localStorage.setItem(DISMISS_KEY, "1");
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="portal-install-prompt"
      className="fixed bottom-4 right-4 z-40 max-w-xs bg-black/95 border border-primary-orange backdrop-blur px-3 py-3 shadow-2xl"
      style={{ boxShadow: "0 0 24px rgba(245,124,0,0.35)" }}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        data-testid="portal-install-dismiss"
        className="absolute top-1 right-1 text-muted-industrial hover:text-red-400"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start gap-2">
        <Download className="w-4 h-4 text-primary-orange mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="mono text-[11px] uppercase tracking-widest text-primary-orange font-bold">
            Install Portal App
          </div>
          {iosHint ? (
            <div className="text-[11px] text-ink mt-1 leading-snug">
              Tap the <span className="mono">Share</span> icon in Safari, then
              choose <span className="mono">Add to Home Screen</span> for one-tap access.
            </div>
          ) : (
            <>
              <div className="text-[11px] text-ink mt-1 leading-snug">
                Get one-tap access to orders, live tracking & invoices — installs
                like a native app.
              </div>
              <button
                type="button"
                onClick={install}
                data-testid="portal-install-btn"
                className="mt-2 mono text-[10px] uppercase tracking-widest px-2.5 py-1 bg-primary-orange text-black font-bold hover:bg-[rgb(var(--primary-hover))]"
              >
                Add to Home Screen
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
